import { useEffect, useMemo, useState } from 'react'
import { loadUsageIntelligence, type UsageIntelligence } from '../../lib/usage'

const PERIODS = [7, 15, 30] as const

function formatDate(value: string | null) {
  if (!value) return '—'
  return new Date(value).toLocaleString('pt-BR')
}

function formatDay(value: string) {
  const datePart = value.slice(0, 10)
  const [year, month, day] = datePart.split('-')
  if (!year || !month || !day) return '—'
  return `${day}/${month}`
}

function usageErrorMessage(error: unknown) {
  const message = error instanceof Error
    ? error.message
    : typeof error === 'object' && error && 'message' in error
      ? String((error as { message?: unknown }).message ?? '')
      : String(error ?? '')

  const normalized = message.toLocaleLowerCase('pt-BR')
  if (
    normalized.includes('usage_intelligence') ||
    normalized.includes('schema cache') ||
    normalized.includes('could not find the function')
  ) {
    return 'A estrutura da Inteligência de Uso precisa ser atualizada no Supabase. Aplique a migration mais recente de usage_intelligence e atualize esta página.'
  }

  return message || 'Não foi possível carregar a Inteligência de Uso.'
}

function Bar({ value, max }: { value: number; max: number }) {
  const width = value > 0 && max > 0 ? Math.max(4, (value / max) * 100) : 0
  return <span className="usage-bar"><i style={{ width: `${width}%` }} /></span>
}

export function UsageIntelligencePanel() {
  const [days, setDays] = useState<number>(30)
  const [data, setData] = useState<UsageIntelligence | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError('')
    loadUsageIntelligence(days)
      .then((result) => mounted && setData(result))
      .catch((err) => mounted && setError(usageErrorMessage(err)))
      .finally(() => mounted && setLoading(false))
    return () => { mounted = false }
  }, [days])

  const maxDaily = useMemo(() => Math.max(1, ...(data?.daily.map((item) => item.sessions) ?? [1])), [data])
  const maxHourly = useMemo(() => Math.max(1, ...(data?.hourly.map((item) => item.sessions) ?? [1])), [data])
  const dailyNewestFirst = useMemo(() => [...(data?.daily ?? [])].reverse(), [data])
  const selectedUnique = days === 7 ? data?.summary.unique_7 : days === 15 ? data?.summary.unique_15 : data?.summary.unique_30
  const selectedSessions = days === 7 ? data?.summary.sessions_7 : days === 15 ? data?.summary.sessions_15 : data?.summary.sessions_30

  return (
    <section className="usage-intelligence">
      <div className="admin-section-title usage-heading">
        <div>
          <p className="eyebrow">INTELIGÊNCIA DE USO</p>
          <h2>Adoção e utilização do Suite</h2>
          <p className="muted">Indicadores nativos do ambiente online. O Suite registra somente sessão, usuário autenticado, módulo e horário.</p>
        </div>
        <div className="usage-periods" aria-label="Período da análise">
          {PERIODS.map((period) => <button key={period} className={days === period ? 'active' : ''} onClick={() => setDays(period)}>{period} dias</button>)}
        </div>
      </div>

      {loading && <div className="empty-state">Carregando inteligência de uso...</div>}
      {error && <div className="notice error">{error}</div>}

      {!loading && data && (
        <>
          <div className="usage-kpi-grid">
            <article><span>Usuários únicos no período</span><strong>{selectedUnique ?? 0}</strong><small>{data.summary.unique_today} usuários hoje</small></article>
            <article><span>Sessões no período</span><strong>{selectedSessions ?? 0}</strong><small>{data.summary.workspace_entries} entradas no Atendimento</small></article>
            <article><span>Utilizações de módulos</span><strong>{data.summary.module_views}</strong><small>{data.summary.admin_views} acessos a áreas administrativas</small></article>
            <article><span>Adoção em 30 dias</span><strong>{Number(data.summary.adoption_30).toLocaleString('pt-BR')}%</strong><small>{data.summary.unique_30} de {data.summary.registered_active} usuários ativos</small></article>
          </div>

          <div className="usage-window-grid">
            <article className="usage-window-card"><span>Últimos 7 dias</span><strong>{data.summary.sessions_7}</strong><small>{data.summary.unique_7} usuários únicos</small></article>
            <article className="usage-window-card"><span>Últimos 15 dias</span><strong>{data.summary.sessions_15}</strong><small>{data.summary.unique_15} usuários únicos</small></article>
            <article className="usage-window-card"><span>Últimos 30 dias</span><strong>{data.summary.sessions_30}</strong><small>{data.summary.unique_30} usuários únicos</small></article>
            <article className="usage-window-card"><span>Último registro</span><strong className="usage-date-value">{formatDate(data.summary.last_event_at)}</strong><small>atividade mais recente</small></article>
          </div>

          <div className="usage-grid-two">
            <section className="usage-panel">
              <div className="usage-panel-head"><div><p className="eyebrow">EVOLUÇÃO</p><h3>Sessões por dia</h3></div><span>{days} dias</span></div>
              <div className="usage-daily-chart">
                {dailyNewestFirst.map((item) => (
                  <div className="usage-chart-row" key={item.date}>
                    <span>{formatDay(item.date)}</span>
                    <Bar value={item.sessions} max={maxDaily} />
                    <strong>{item.sessions}</strong>
                    <small>{item.unique_users} únicos</small>
                  </div>
                ))}
              </div>
            </section>

            <section className="usage-panel">
              <div className="usage-panel-head"><div><p className="eyebrow">HORÁRIOS</p><h3>Picos de utilização</h3></div><span>sessões</span></div>
              <div className="usage-hour-grid">
                {data.hourly.map((item) => {
                  const height = item.sessions > 0 ? Math.max(4, (item.sessions / maxHourly) * 100) : 0
                  return (
                    <div key={item.hour} title={`${item.hour}h: ${item.sessions} sessões`}>
                      <i style={{ height: `${height}%`, minHeight: item.sessions > 0 ? '4px' : '0' }} />
                      <span>{String(item.hour).padStart(2, '0')}</span>
                    </div>
                  )
                })}
              </div>
            </section>
          </div>

          <div className="usage-grid-two">
            <section className="usage-panel">
              <div className="usage-panel-head"><div><p className="eyebrow">MÓDULOS OPERACIONAIS</p><h3>Mais utilizados</h3></div><span>{data.modules.length} módulos</span></div>
              <div className="usage-ranking-list">
                {data.modules.length === 0 && <div className="empty-state">Ainda não há abertura de módulos registrada.</div>}
                {data.modules.map((item, index) => (
                  <article key={item.module_key}>
                    <b className="usage-rank">{index + 1}</b>
                    <div><strong>{item.module_label}</strong><small>{item.unique_users} usuários únicos · último acesso {formatDate(item.last_access)}</small></div>
                    <em>{item.accesses}</em>
                  </article>
                ))}
              </div>
            </section>

            <section className="usage-panel">
              <div className="usage-panel-head"><div><p className="eyebrow">ÁREAS ADMINISTRATIVAS</p><h3>Mais acessadas</h3></div><span>{data.admin_areas.length} áreas</span></div>
              <div className="usage-ranking-list">
                {data.admin_areas.length === 0 && <div className="empty-state">Ainda não há navegação administrativa registrada.</div>}
                {data.admin_areas.map((item, index) => (
                  <article key={item.module_key}>
                    <b className="usage-rank">{index + 1}</b>
                    <div><strong>{item.module_label}</strong><small>{item.unique_users} usuários únicos · último acesso {formatDate(item.last_access)}</small></div>
                    <em>{item.accesses}</em>
                  </article>
                ))}
              </div>
            </section>
          </div>

          <section className="usage-panel">
            <div className="usage-panel-head"><div><p className="eyebrow">RANKING DE USUÁRIOS</p><h3>Quem mais utiliza</h3></div><span>{days} dias</span></div>
            <div className="usage-ranking-list usage-user-ranking">
              {data.users.length === 0 && <div className="empty-state">Ainda não há sessões registradas.</div>}
              {data.users.map((item, index) => (
                <article key={item.user_id}>
                  <b className="usage-rank">{index + 1}</b>
                  <div>
                    <strong>{item.nome || item.email}</strong>
                    <small>{item.role} · {item.activity_total} atividades · {item.sessions} sessões · {item.active_days} dias ativos · {item.modules} módulos · último acesso {formatDate(item.last_access)}</small>
                  </div>
                  <em>{item.activity_total}</em>
                </article>
              ))}
            </div>
          </section>

          <section className="usage-panel usage-adoption-panel">
            <div className="usage-panel-head"><div><p className="eyebrow">ADOÇÃO E INATIVIDADE</p><h3>Usuários ativos x cadastrados</h3></div><span>{data.summary.registered_active} contas ativas</span></div>
            <div className="usage-adoption-grid">
              <article><span>Acessaram em 30 dias</span><strong>{data.summary.unique_30}</strong><small>{Number(data.summary.adoption_30).toLocaleString('pt-BR')}% de adoção</small></article>
              <article><span>Sem acesso há 15 dias</span><strong>{data.inactivity.inactive_15}</strong><small>contas ativas sem sessão recente</small></article>
              <article><span>Sem acesso há 30 dias</span><strong>{data.inactivity.inactive_30}</strong><small>atenção para adoção/necessidade de acesso</small></article>
              <article><span>Nunca acessaram</span><strong>{data.inactivity.never_accessed}</strong><small>desde o início deste registro</small></article>
            </div>
          </section>
        </>
      )}
    </section>
  )
}
